import React, { useState } from 'react';
import LookThumb from '../shared/LookThumb';
import LookPreviewCanvas from '../shared/LookPreviewCanvas';
import BackgroundPicker from './BackgroundPicker';

const SlotCard = React.memo(({ slot, looks, onSlotChange, onBackgroundChange, backgroundSetId, backgroundsById, lookUsageCounts, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [showBgPicker, setShowBgPicker] = useState(false);
  const assignedLook = looks.find(l => l.id === slot.look_template_id);
  // Prefer the server-provided URL; fall back to the client-side id→URL map so
  // saved backgrounds still render if the layout response arrives with a null
  // URL (transient Paperclip/S3 hiccup). Truth of record is slot.background_id.
  const bgFallback = slot.background_id ? (backgroundsById || {})[slot.background_id] : null;
  const backgroundImageUrl = slot.background_image_url || bgFallback?.image_url || null;
  const isLandscape = slot.slot_type === 'landscape';
  const hasBg = !!backgroundImageUrl;
  const hasContent = !!assignedLook || hasBg;

  const thumbW = isLandscape ? 200 : 120;
  const thumbH = isLandscape ? 160 : 150;

  return (
    <div
      draggable={hasContent}
      onDragStart={() => onDragStart?.(slot.slot_id)}
      onDragOver={e => { e.preventDefault(); onDragOver?.(slot.slot_id); }}
      onDrop={e => { e.preventDefault(); onDrop?.(slot.slot_id); }}
      onDragEnd={onDragEnd}
      style={{
        flex: isLandscape ? 2 : 1,
        border: isDragOver ? '2px dashed #1976d2' : '1px solid #e0e0e0',
        borderRadius: 6,
        background: isDragOver ? '#e3f2fd' : '#fff',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: 100,
        position: 'relative',
        cursor: hasContent ? 'grab' : 'default',
        transition: 'border-color .15s, background .15s'
      }}>
      <div style={{ fontSize: 10, color: '#999', marginBottom: 6 }}>
        {slot.slot_id} ({slot.slot_type})
      </div>

      {assignedLook ? (
        <div style={{ textAlign: 'center', width: '100%' }}>
          {/* Thumbnail area — background sits behind the look preview here */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: thumbW, height: thumbH, margin: '0 auto',
            borderRadius: 3, overflow: 'hidden', position: 'relative',
            background: hasBg ? undefined : '#f0f0f0',
            backgroundImage: hasBg ? `url(${backgroundImageUrl})` : undefined,
            backgroundSize: hasBg ? 'cover' : undefined,
            backgroundPosition: hasBg ? 'center' : undefined,
          }}>
            <LookPreviewCanvas
              look={assignedLook}
              width={thumbW}
              height={thumbH}
              transparent
              allLooks={looks}
            />
          </div>
          <div style={{ fontSize: 11, fontWeight: 500, marginTop: 4, color: '#333' }}>
            {assignedLook.name}
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 4, justifyContent: 'center' }}>
            <button
              title="Change"
              onClick={() => setShowPicker(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, background: '#e3f2fd',
                border: '1px solid #90caf9', borderRadius: 4, cursor: 'pointer'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              </svg>
            </button>
            <button
              title="Clear"
              onClick={() => onSlotChange(slot.slot_id,null)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, background: '#ffebee',
                border: '1px solid #ef9a9a', borderRadius: 4, cursor: 'pointer'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d32f2f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
            <button
              onClick={() => setShowBgPicker(prev => !prev)}
              style={{
                height: 24, padding: '0 8px', fontSize: 10,
                background: '#f5f5f5',
                border: '1px solid #ccc',
                borderRadius: 3, cursor: 'pointer',
                fontWeight: 400,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                color: '#666'
              }}
              title="Background"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              Background
            </button>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', gap: 4 }}>
          <button
            onClick={() => setShowPicker(true)}
            style={{
              padding: '12px 16px', background: '#f5f5f5',
              border: '2px dashed #ccc',
              borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#999',
              width: '100%'
            }}
          >
            + Assign Look
          </button>
          <button
            onClick={() => setShowBgPicker(prev => !prev)}
            style={{
              height: 24, padding: '0 8px', fontSize: 10,
              background: hasBg ? '#e8f5e9' : '#f5f5f5',
              border: `1px solid ${hasBg ? '#81c784' : '#ccc'}`,
              borderRadius: 3, cursor: 'pointer',
              fontWeight: hasBg ? 600 : 400,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
              color: hasBg ? '#2e7d32' : '#666'
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            Background
          </button>
        </div>
      )}

      {/* Look picker dropdown */}
      {showPicker && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: '#fff', border: '1px solid #ccc', borderRadius: 4,
          zIndex: 100, maxHeight: 300, overflow: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,.15)', padding: 8
        }}>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 6, fontWeight: 500 }}>
            Select a look:
          </div>
          {looks.length === 0 && (
            <div style={{ padding: 8, color: '#999', fontSize: 12 }}>No looks available</div>
          )}
          {looks.map(look => (
            <div
              key={look.id}
              onClick={() => { onSlotChange(slot.slot_id,look.id); setShowPicker(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px',
                cursor: 'pointer', borderRadius: 3
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <LookThumb look={look} size={36} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{look.name}</div>
                <div style={{ fontSize: 10, color: '#999' }}>{look.season} {look.pose_number}</div>
              </div>
              {(lookUsageCounts || {})[look.id] > 0 && (
                <span style={{
                  fontSize: 9, background: '#e3f2fd', color: '#1565c0',
                  borderRadius: 8, padding: '1px 6px', fontWeight: 600, flexShrink: 0
                }}>
                  {lookUsageCounts[look.id]}x
                </span>
              )}
            </div>
          ))}
          <button
            onClick={() => setShowPicker(false)}
            style={{
              marginTop: 6, width: '100%', padding: '4px', fontSize: 11,
              background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 3,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Background picker */}
      {showBgPicker && (
        <BackgroundPicker
          backgroundSetId={backgroundSetId}
          onSelect={(bgId, bgUrl) => {
            onBackgroundChange(slot.slot_id, bgId, bgUrl);
            setShowBgPicker(false);
          }}
          onClose={() => setShowBgPicker(false)}
        />
      )}
    </div>
  );
});

SlotCard.displayName = 'SlotCard';

export default SlotCard;
